// src/components/MFA/SessionsList.tsx
import { FC, useContext, useEffect, useState } from "react";
import { mfaService, SessionInfo } from '../api';
import Button, { Button_Style, Button_Type } from 'shared/ui/Button/Button';
import ConfirmModal from 'features/mfa/components/ConfirmModal/ConfirmModal';
import './SessionsList.scss';
import { UserContext } from "features/account";

interface SessionsListProps {
  onClose: () => void;
}

interface ConfirmDialogState {
  isOpen: boolean;
  type: 'revoke' | 'revokeAll' | 'report';
  sessionId?: string;
  deviceName?: string;
  sessionCount?: number;
}

const SessionsList: FC<SessionsListProps> = ({ onClose }) => {
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    isOpen: false,
    type: 'revoke'
  });

  const { user, userType } = useContext(UserContext);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const response = await mfaService.getSessions(userType ?? 'individual');
      setSessions(response.sessions);
      setCurrentSessionId(response.currentSessionId);
    } catch (err: any) {
      setError('Erreur lors du chargement des sessions');
    } finally {
      setLoading(false);
    }
  };

  const openRevokeConfirm = (sessionId: string, deviceName: string) => {
    setConfirmDialog({
      isOpen: true,
      type: 'revoke',
      sessionId,
      deviceName
    });
  };

  const openRevokeAllConfirm = () => {
    const otherSessionsCount = sessions.filter(s => !s.isCurrent).length;
    setConfirmDialog({
      isOpen: true,
      type: 'revokeAll',
      sessionCount: otherSessionsCount
    });
  };

  const openReportConfirm = (sessionId: string, deviceName: string) => {
    setConfirmDialog({
      isOpen: true,
      type: 'report',
      sessionId,
      deviceName
    });
  };

  const handleConfirm = async () => {
    const { type, sessionId, deviceName, sessionCount } = confirmDialog;

    if (type === 'revoke' && sessionId) {
      setActionLoading(sessionId);
      try {
        await mfaService.revokeSession(sessionId, userType ?? 'individual');
        await loadSessions();
      } catch (err: any) {
        setError('Erreur lors de la déconnexion');
        setTimeout(() => setError(''), 3000);
      } finally {
        setActionLoading(null);
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      }
    }

    if (type === 'revokeAll') {
      setActionLoading('all');
      try {
        await mfaService.revokeAllSessions(userType ?? 'individual');
        await loadSessions();
      } catch (err: any) {
        setError('Erreur lors de la déconnexion');
        setTimeout(() => setError(''), 3000);
      } finally {
        setActionLoading(null);
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      }
    }

    if (type === 'report' && sessionId) {
      setActionLoading(sessionId);
      try {
        const response = await mfaService.reportSession(sessionId, 'RAS', userType ?? 'individual');
        if (response.recommendPasswordChange) {
          // Show success message
          setError('Session signalée. Nous vous recommandons de changer votre mot de passe.');
          setTimeout(() => setError(''), 5000);
        }
        await loadSessions();
      } catch (err: any) {
        setError('Erreur lors du signalement');
        setTimeout(() => setError(''), 3000);
      } finally {
        setActionLoading(null);
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      }
    }
  };

  const handleCancel = () => {
    setConfirmDialog({ ...confirmDialog, isOpen: false });
  };

  const getConfirmDialogContent = () => {
    const { type, deviceName, sessionCount } = confirmDialog;

    switch (type) {
      case 'revoke':
        return {
          title: 'Déconnexion',
          message: `Voulez-vous vraiment déconnecter "${deviceName}" ?`,
          confirmLabel: 'Déconnecter',
          confirmStyle: Button_Type.Error
        };
      case 'revokeAll':
        return {
          title: 'Déconnexion multiple',
          message: `Voulez-vous vraiment déconnecter tous les autres appareils (${sessionCount}) ?`,
          confirmLabel: 'Tout déconnecter',
          confirmStyle: Button_Type.REVERSE_ERROR
        };
      case 'report':
        return {
          title: 'Signaler un appareil suspect',
          message: `Signaler "${deviceName}" comme suspect ?\n\nUn email d'alerte sera envoyé à votre adresse.`,
          confirmLabel: 'Signaler',
          confirmStyle: Button_Type.Error
        };
      default:
        return {
          title: 'Confirmation',
          message: 'Confirmez-vous cette action ?',
          confirmLabel: 'Confirmer',
          confirmStyle: Button_Type.PRIMARY
        };
    }
  };

  const dialogContent = getConfirmDialogContent();

  if (loading) {
    return <div className="text-center p-4">Chargement des sessions...</div>;
  }

  return (
    <>
      <div className="sessions-list">
        <div className="sessions-header gap-2">
          <h3>Appareils connectés</h3>
          {sessions.filter(s => !s.isCurrent).length > 0 && (
            <Button
              type={Button_Type.PRIMARY}
              style={Button_Style.OUTLINED}
              label="Déconnecter tous les autres"
              onClick={openRevokeAllConfirm}
              disabled={actionLoading !== null}
            />
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="sessions-grid">
          {sessions.map((session) => (
            <div key={session.id} className={`session-item ${session.isCurrent ? 'current' : ''}`}>
              <div className="session-device">
                <div className="device-icon">
                  {session.deviceInfo.device === 'mobile' ? '📱' :
                    session.deviceInfo.device === 'tablet' ? '📟' : '💻'}
                </div>
                <div className="device-info">
                  <div className="device-name">
                    {session.deviceInfo.deviceName}
                    {session.isCurrent && <span className="current-badge">En cours</span>}
                  </div>
                  <div className="device-details">
                    <div>{session.deviceInfo.browser}</div>
                    <div>IP: {session.ipAddress}</div>
                    <div>Dernière activité: {session.lastActivityFormatted}</div>
                  </div>
                </div>
              </div>
              <div className="session-actions">
                {!session.isCurrent && (
                  <>
                    <Button
                      type={Button_Type.Error}
                      style={Button_Style.OUTLINED}
                      label={actionLoading === session.sessionId ? "..." : "Déconnecter"}
                      onClick={() => openRevokeConfirm(session.sessionId, session.deviceInfo.deviceName)}
                      disabled={actionLoading !== null}
                      size="small"
                    />
                    <Button
                      type={Button_Type.SECONDARY}
                      style={Button_Style.OUTLINED}
                      label={actionLoading === session.sessionId ? "..." : "Signaler"}
                      onClick={() => openReportConfirm(session.sessionId, session.deviceInfo.deviceName)}
                      disabled={actionLoading !== null}
                      size="small"
                    />
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {sessions.length === 0 && (
          <div className="text-center p-4">
            Aucune session active trouvée.
          </div>
        )}
      </div>

      {confirmDialog.isOpen && (
        <ConfirmModal
          isOpen={confirmDialog.isOpen}
          onClose={handleCancel}
          onConfirm={handleConfirm}
          title={dialogContent.title}
          message={dialogContent.message}
          confirmLabel={dialogContent.confirmLabel}
          cancelLabel="Annuler"
          confirmType={dialogContent.confirmStyle}
        />
      )}
    </>
  );
};

export default SessionsList;